/**
 * Entrance system, site-wide.
 *
 * Three shapes of markup, one mechanism:
 *   data-reveal          — the element itself comes in
 *   data-reveal          + .reveal-word children — the heading assembles
 *   data-reveal-stagger  — rows come in one after another
 *
 * Its own bundle (reveal.js / reveal.css) because it is not tied to any block:
 * anything can opt in by marking an element, and the resting state has to be in
 * the cascade before first paint.
 */

import { STAGGER as STAGGER_STEPS } from './utils/motion';

// Announced before anything else runs: the stylesheet's four-second failsafe is
// scoped to the absence of this class, and leaving it unset would uncover every
// element on the page regardless of where the visitor has scrolled.
document.documentElement.classList.add('has-reveal-js');

const { element: STAGGER, word: WORD_STAGGER, row: ROW_STAGGER } = STAGGER_STEPS;

// How far into the viewport an element has to travel before it is let in.
const ENTER_RATIO = 0.82;

/**
 * Reveal an element and announce it, so a block can react to being seen without
 * repeating the on-screen test.
 *
 * @param element Element to reveal.
 * @param delay   Milliseconds to wait first.
 */
const show = (element: Element, delay: number): void => {
  window.setTimeout(() => {
    element.classList.add('is-revealed');
    element.dispatchEvent(new CustomEvent('goodface:revealed', { bubbles: true }));
  }, delay);
};

/**
 * Distance from the top of the viewport to where the element sits *in the
 * layout*, ignoring any transform on it.
 *
 * getBoundingClientRect() reports the transformed box, and the resting state of
 * a reveal is a transform — an element pushed down 46vh reads as off-screen and
 * so never qualifies for the reveal that would bring it back. It hides from its
 * own trigger. offsetTop is layout-only and immune to that.
 *
 * @param element Element to measure.
 */
const layoutTop = (element: HTMLElement): number => {
  let top = 0;
  let node: HTMLElement | null = element;

  while (node) {
    top += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }

  return top - window.scrollY;
};

/**
 * Reveal one element and report how long it occupies, so whatever comes next
 * can queue behind it.
 *
 * @param element Element carrying data-reveal or data-reveal-stagger.
 * @param delay   Milliseconds to wait before it starts.
 * @return Milliseconds to add before the following element starts.
 */
const reveal = (element: HTMLElement, delay: number): number => {
  if (element.hasAttribute('data-reveal-stagger')) {
    const rows = Array.from(element.children);

    rows.forEach((row, index) => show(row, delay + index * ROW_STAGGER));

    return rows.length * ROW_STAGGER + STAGGER;
  }

  const words = element.querySelectorAll<HTMLElement>('.reveal-word');

  if (!words.length) {
    show(element, delay);
    return STAGGER;
  }

  element.classList.add('is-revealed');
  words.forEach((word, index) => show(word, delay + index * WORD_STAGGER));

  return words.length * WORD_STAGGER + STAGGER;
};

const start = (): void => {
  let waiting = Array.from(
    document.querySelectorAll<HTMLElement>('[data-reveal], [data-reveal-stagger]')
  );

  if (!waiting.length) {
    return;
  }

  let delay = 0;

  waiting = waiting.filter((element) => {
    const top = layoutTop(element);

    // Already scrolled past — a reload restores the position, and anything above
    // the screen missed its entrance. Put it straight in, and keep it out of the
    // sequence: queueing a screenful of blocks nobody can see makes the ones
    // actually in view wait their turn behind them.
    if (top + element.offsetHeight <= 0) {
      reveal(element, 0);
      return false;
    }

    // Anything whose box begins on the first screen belongs to the opening
    // sequence, even when its resting transform parks it below the fold.
    if (top >= window.innerHeight) {
      return true;
    }

    delay += reveal(element, delay);
    return false;
  });

  if (!waiting.length) {
    return;
  }

  let queued = false;

  const check = (): void => {
    queued = false;

    waiting = waiting.filter((element) => {
      if (layoutTop(element) > window.innerHeight * ENTER_RATIO) {
        return true;
      }

      reveal(element, 0);
      return false;
    });

    if (!waiting.length) {
      window.removeEventListener('scroll', request);
      window.removeEventListener('resize', request);
    }
  };

  function request(): void {
    if (queued) {
      return;
    }

    queued = true;
    window.requestAnimationFrame(check);
  }

  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request);
  check();
};

// The preloader hands over before the sequence starts so the two never play
// over each other. The attribute covers the case where it has already finished
// by the time this script runs and the event is long gone.
const preloader = document.querySelector('[data-preloader]');

if (preloader && !preloader.hasAttribute('data-preloader-done')) {
  document.addEventListener('goodface:preloader-done', start, { once: true });
} else {
  start();
}

export {};
